import { useRef, useState } from "react";
import styled from "styled-components";
import { GiFiles } from "react-icons/gi";
import { useComparisonState } from "../../hooks/useComparisonState";
import { useComparisonMode } from "../../hooks/useComparisonMode";
import { media } from "../../../../helpers/responsive";
import OptionsList from "./OptionsList";
import ExitComparisonButton from "../ExitComparisonButton";
import type { ComparisonOption, DaySubRange } from "../../types";

const ComparisonSelect = () => {
  const comparison = useComparisonState();
  const detectedMode = useComparisonMode();
  const [isOpen, setIsOpen] = useState(false);
  const [pendingOption, setPendingOption] = useState<ComparisonOption | null>(null);
  const [pendingConfig, setPendingConfig] = useState<any>({});
  const dropdownRef = useRef<HTMLDivElement>(null);
  const isDisabled = !detectedMode;

  const handleSelect = (option: ComparisonOption, config?: any) => {
    setPendingOption(option);
    setPendingConfig(config || {});
  };

  const handleApply = () => {
    if (!detectedMode || !pendingOption) return;
    comparison.activate({
      mode: detectedMode,
      option: pendingOption,
      count: pendingConfig.count,
      subRange: pendingConfig.subRange as DaySubRange,
      customFrom: pendingConfig.customFrom,
      customTo: pendingConfig.customTo,
    });
    setIsOpen(false);
    setPendingOption(null);
    setPendingConfig({});
  };

  const handleToggle = () => {
    if (isDisabled) return;
    setIsOpen((o) => !o);
  };

  if (comparison.isActive) {
    return <ExitComparisonButton />;
  }

  return (
    <Wrapper ref={dropdownRef}>
      <button
        className={`trigger-btn ${isDisabled ? "disabled" : ""}`}
        onClick={handleToggle}
        title={isDisabled ? "Select a valid date range to compare" : "Compare periods"}
      >
        <GiFiles fontSize="22px" />
      </button>

      {isOpen && detectedMode && (
        <>
          <div className="backdrop" onClick={() => setIsOpen(false)} />
          <div className="dropdown-panel">
            <div className="panel-title">Compare to:</div>
            <OptionsList
              mode={detectedMode}
              selectedOption={pendingOption}
              onSelect={handleSelect}
            />
            <button
              className="apply-btn"
              onClick={handleApply}
              disabled={!pendingOption}
            >
              Apply
            </button>
          </div>
        </>
      )}
    </Wrapper>
  );
};

export default ComparisonSelect;

const Wrapper = styled.div`
  position: relative;

  .trigger-btn {
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 6px 10px;
    border: 1px solid transparent;
    border-radius: 8px;
    background: ${({ theme }) => theme.widget.background};
    color: #0D8A6A;
    cursor: pointer;
    transition: all 0.15s;

    &:hover:not(.disabled) {
      border-color: #0D8A6A;
    }

    &.disabled {
      opacity: 0.4;
      cursor: default;
    }

    svg path {
      stroke: #0D8A6A;
    }
  }

  .backdrop {
    position: fixed;
    inset: 0;
    z-index: 999;
  }

  .dropdown-panel {
    position: absolute;
    top: calc(100% + 8px);
    right: 0;
    z-index: 1000;
    min-width: 320px;
    padding: 20px;
    background: ${({ theme }) => theme.widget.background};
    border-radius: 12px;
    border: 1px solid ${({ theme }) => theme.border_color || "#e0e0e0"};
    box-shadow: 0 8px 32px rgba(0, 0, 0, 0.12);
  }

  .panel-title {
    font-size: 16px;
    font-weight: 600;
    color: ${({ theme }) => theme.text};
    margin-bottom: 16px;
  }

  .apply-btn {
    width: 100%;
    height: 40px;
    margin-top: 16px;
    border: none;
    border-radius: 8px;
    background: #0D8A6A;
    color: #fff;
    font-size: 14px;
    font-weight: 600;
    cursor: pointer;
    transition: background 0.15s;

    &:hover:not(:disabled) {
      background: #0a7359;
    }

    &:disabled {
      opacity: 0.5;
      cursor: default;
    }
  }

  ${media.down.xs} {
    .dropdown-panel {
      position: fixed;
      top: auto;
      bottom: 0;
      left: 0;
      right: 0;
      min-width: unset;
      border-radius: 16px 16px 0 0;
      max-height: 70vh;
      overflow-y: auto;
    }
  }
`;
